import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Trash2, Download, MailCheck, MailWarning, ShieldCheck, Loader2 } from 'lucide-react';
import SettingCard from '../../components/settings/SettingCard';
import SettingsRow from '../../components/settings/SettingsRow';
import { useAuth } from '../../hooks/useAuth';
import { useSettingsStore } from '../../store/settingsStore';

export default function Account() {
  const navigate = useNavigate();
  const { user, signOut, sendEmailVerification } = useAuth();
  const { data } = useSettingsStore();
  const [signingOut, setSigningOut] = useState(false);
  const [verifySent, setVerifySent] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const email = user?.email ?? data?.profile.email ?? '';
  const verified = user?.emailVerified ?? false;

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate('/auth/login', { replace: true });
    } finally {
      setSigningOut(false);
    }
  };

  const resendVerification = async () => {
    setVerifying(true);
    try {
      await sendEmailVerification();
      setVerifySent(true);
    } catch {
      /* surfaced elsewhere; keep this action lightweight */
    } finally {
      setVerifying(false);
    }
  };

  const downloadData = () => {
    const blob = new Blob([JSON.stringify({ profile: data?.profile, preferences: data?.preferences, exportedAt: new Date().toISOString() }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'messego-data.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="hidden lg:block">
        <h1 className="text-2xl font-bold tracking-tight">Account</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Session, data export and account deletion</p>
      </div>

      <SettingCard>
        <div className="flex items-center gap-4">
          <img src={user?.photoURL || data?.profile.avatarUrl || 'https://picsum.photos/seed/user/200/200'} alt="You" className="w-14 h-14 rounded-2xl object-cover" referrerPolicy="no-referrer" />
          <div className="min-w-0">
            <p className="font-bold truncate">{user?.displayName || data?.profile.displayName || 'Your account'}</p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">{email}</p>
          </div>
        </div>
      </SettingCard>

      {/* Email verification status */}
      <SettingCard flush>
        <div className="flex items-center justify-between gap-4 px-5 py-4">
          <div className="flex items-center gap-3.5">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${verified ? 'bg-green-50 text-green-600' : 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400'}`}>
              {verified ? <MailCheck className="w-5 h-5" /> : <MailWarning className="w-5 h-5" />}
            </div>
            <div>
              <p className="font-semibold text-sm">{verified ? 'Email verified' : 'Email not verified'}</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">{verified ? 'Your email address is confirmed' : verifySent ? 'Verification email sent — check your inbox' : 'Verify to secure your account'}</p>
            </div>
          </div>
          {!verified && (
            <button onClick={resendVerification} disabled={verifying || verifySent} className="text-xs font-semibold text-neutral-900 dark:text-neutral-50 rounded-xl px-3 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors disabled:opacity-50">
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : verifySent ? 'Sent' : 'Resend'}
            </button>
          )}
        </div>
      </SettingCard>

      <SettingCard title="Your data" flush>
        <SettingsRow icon={Download} label="Download your data" description="Export a copy of your profile and preferences" onClick={downloadData} />
        <SettingsRow icon={ShieldCheck} label="Manage security" description="Password, 2FA and sessions" to="/app/settings/security" />
      </SettingCard>

      <SettingCard title="Session" flush>
        <SettingsRow icon={signingOut ? Loader2 : LogOut} label={signingOut ? 'Signing out…' : 'Sign out'} description="Sign out of this device" onClick={handleSignOut} danger />
      </SettingCard>

      <SettingCard title="Danger zone" flush>
        <SettingsRow icon={Trash2} label="Delete account" description="Permanently remove your account and data" to="/app/settings/account/delete" danger />
      </SettingCard>
    </div>
  );
}
